/**
 * useFileUpload hook - App-specific wrapper around @f2e/ui useAttachmentUpload
 *
 * This hook provides file upload functionality for the web management console,
 * using the shared useAttachmentUpload hook from @f2e/ui with app-specific:
 * - Workspace-scoped uploads via /retrieval/upload-file
 * - Evaluation context support
 *
 * Default limits (from shared hook): maxFiles=10, maxFileSize=50MB
 */
import { useCallback } from "react"
import {
  SUPPORTED_ATTACHMENT_MIME_TYPES,
  SUPPORTED_MIME_TYPES,
} from "@f2e/shared/constants"
import {
  buildAttachmentMeta,
  buildDisplayAttachmentMeta,
} from "@f2e/shared/utils"

import {
  useAttachmentUpload,
  type UseAttachmentUploadOptions as SharedOptions,
  type UseAttachmentUploadReturn,
  type UploadResult,
  type UploadError,
  type UploadErrorType,
} from "@f2e/ui/hooks"

// Re-export error types for consumers
export type { UploadError, UploadErrorType }

import { Workspace } from "@/types"
import {
  useFileUploadApi,
  fileUploadResponseToFileBadgeData,
} from "@/hooks/api"
import i18n from "@/i18n"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface UseFileUploadOptions {
  /** Workspace context for uploads (required for API) */
  workspace?: Workspace
  /** Maximum number of files allowed (default: 10 from shared hook) */
  maxFiles?: number
  /** Maximum file size in bytes (default: 50MB from shared hook) */
  maxFileSize?: number
  /** Whether this is used in evaluation context */
  isEvaluationContext?: boolean
  /** Whether audio attachments are allowed for the current agent/model. */
  allowAudio?: boolean
  /** Callback when upload error occurs (receives structured error object) */
  onUploadError?: (error: UploadError) => void
}

export const useFileUpload = (
  options: UseFileUploadOptions = {}
): UseAttachmentUploadReturn => {
  const {
    workspace,
    maxFiles,
    maxFileSize,
    isEvaluationContext = false,
    allowAudio = true,
    onUploadError,
  } = options

  const { fetchFn: uploadFileApi } = useFileUploadApi()

  // Create upload function that wraps web's callback-based API into a Promise
  const uploadFn = useCallback(
    async (file: File): Promise<UploadResult> => {
      if (!workspace) {
        throw new Error("Workspace is required for upload")
      }
      const uploadMeta = await buildAttachmentMeta(file)

      const formData = new FormData()
      formData.append("file", file)
      formData.append(
        "data",
        JSON.stringify({
          workspace_id: workspace.id,
          is_evaluation_context: isEvaluationContext,
          meta: uploadMeta,
        })
      )

      return new Promise<UploadResult>((resolve, reject) => {
        try {
          uploadFileApi({
            body: formData,
            callbackFn: (response) => {
              if (response?.data) {
                const fileBadgeData = fileUploadResponseToFileBadgeData(
                  response.data
                )
                resolve({
                  uuid: fileBadgeData.uuid,
                  url: fileBadgeData.url,
                  meta: fileBadgeData.meta ?? uploadMeta,
                })
              } else {
                reject(new Error(response?.message || "Upload failed"))
              }
            },
          }).catch((error) => {
            const message = getUserFacingApiErrorMessage(
              error,
              i18n.t,
              error instanceof Error ? error.message : "Upload failed"
            )
            reject(new Error(message))
          })
        } catch (err) {
          reject(err instanceof Error ? err : new Error("Upload failed"))
        }
      })
    },
    [workspace, isEvaluationContext, uploadFileApi]
  )

  // Only pass options that are explicitly provided; let shared hook use its defaults
  const sharedOptions: SharedOptions = {
    uploadFn,
    getFileMeta: buildDisplayAttachmentMeta,
    ...(maxFiles !== undefined && { maxFiles }),
    ...(maxFileSize !== undefined && { maxFileSize }),
    allowedTypes: [
      ...(allowAudio ? SUPPORTED_ATTACHMENT_MIME_TYPES : SUPPORTED_MIME_TYPES),
    ],
    onUploadError,
  }

  return useAttachmentUpload(sharedOptions)
}
